import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, FlaskConical, Lock, RefreshCw, Trash2, ExternalLink, Trophy, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MIN_SAMPLE_PER_VARIANT = 100;
const CHI_SQ_95 = 3.841;
const CHI_SQ_99 = 6.635;
const CHI_SQ_999 = 10.828;

/**
 * 2-variant chi-square significance test on a 2x2 contingency table.
 * Returns a verdict you can render: "winner" | "needs_more" | "inconclusive" | null.
 */
function computeSignificance(variantA, variantB, eventName = "unlock_click") {
  if (!variantA || !variantB) return null;
  const n1 = variantA.count ?? 0;
  const n2 = variantB.count ?? 0;
  const a = variantA.conversions ?? 0;
  const c = variantB.conversions ?? 0;
  const b = n1 - a;
  const d = n2 - c;
  const N = n1 + n2;
  if (n1 < MIN_SAMPLE_PER_VARIANT || n2 < MIN_SAMPLE_PER_VARIANT) {
    return { verdict: "needs_more", minSize: MIN_SAMPLE_PER_VARIANT };
  }
  const rateA = n1 > 0 ? a / n1 : 0;
  const rateB = n2 > 0 ? c / n2 : 0;
  if (rateA === rateB) return { verdict: "inconclusive" };
  const denom = (a + b) * (c + d) * (a + c) * (b + d);
  if (denom === 0) return { verdict: "inconclusive" };
  const chiSq = (N * Math.pow(Math.abs(a * d - b * c), 2)) / denom;
  if (chiSq < CHI_SQ_95) return { verdict: "inconclusive", chiSq };
  const winnerId = rateA > rateB ? variantA.id : variantB.id;
  const confidence =
    chiSq >= CHI_SQ_999 ? "99.9%" : chiSq >= CHI_SQ_99 ? "99%" : "95%";
  return {
    verdict: "winner",
    winnerId,
    confidence,
    chiSq,
    uplift:
      rateA > rateB && rateB > 0
        ? ((rateA - rateB) / rateB) * 100
        : rateB > rateA && rateA > 0
        ? ((rateB - rateA) / rateA) * 100
        : null,
    eventName,
  };
}

// Registry of active experiments. Keep in sync with `/app/frontend/src/lib/experiments.js`.
const EXPERIMENTS = [
  {
    key: "teaser_density_v1",
    name: "Teaser density",
    description:
      "Controls how aggressively the free tier is gated after an analysis.",
    variants: [
      { id: "full",    label: "Full",    blurb: "1 rewrite + 2 issues capped, all 4 teaser cards." },
      { id: "minimal", label: "Minimal", blurb: "All rewrites + issues visible, Impact Forecast teaser only." },
    ],
  },
];

const STORAGE_PREFIX = "sib_exp_";

export default function DebugExperimentsPage() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem("sib_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState({});
  const [serverCounts, setServerCounts] = useState({}); // { experiment_key: {total, variants} }
  const [loadingCounts, setLoadingCounts] = useState(false);

  const refreshAssignments = () => {
    const snap = {};
    for (const exp of EXPERIMENTS) {
      try {
        snap[exp.key] = localStorage.getItem(STORAGE_PREFIX + exp.key);
      } catch {
        snap[exp.key] = null;
      }
    }
    setAssignments(snap);
  };

  const refreshServerCounts = async (key) => {
    const useKey = key ?? localStorage.getItem("sib_admin_key");
    if (!useKey) return;
    setLoadingCounts(true);
    try {
      const res = await axios.get(
        `${API}/admin/experiments?admin_key=${encodeURIComponent(useKey)}`
      );
      const map = {};
      for (const row of res.data?.experiments || []) {
        map[row.experiment] = {
          total: row.total,
          variants: row.variants,
          conversions: row.conversions || {},
        };
      }
      setServerCounts(map);
    } catch {
      // silent — server may be warming up; user can hit Refresh
    } finally {
      setLoadingCounts(false);
    }
  };

  useEffect(() => {
    const existing = localStorage.getItem("sib_admin_key");
    if (existing) verify(existing, /* silent */ true);
    refreshAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (key, silent = false) => {
    if (!key.trim()) {
      if (!silent) toast.error("Enter admin password");
      return;
    }
    setLoading(true);
    try {
      await axios.get(`${API}/admin/settings?admin_key=${encodeURIComponent(key)}`);
      localStorage.setItem("sib_admin_key", key);
      setAuthed(true);
      if (!silent) toast.success("Authenticated");
      // Kick off count fetch as soon as we're in.
      refreshServerCounts(key);
    } catch {
      if (!silent) toast.error("Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  const setVariant = (expKey, variantId) => {
    localStorage.setItem(STORAGE_PREFIX + expKey, variantId);
    refreshAssignments();
    toast.success(`Assigned to "${variantId}" — reload the app to see it apply`);
  };

  const clearVariant = (expKey) => {
    localStorage.removeItem(STORAGE_PREFIX + expKey);
    refreshAssignments();
    toast.success("Cleared — next visit will randomly re-roll");
  };

  const signOut = () => {
    localStorage.removeItem("sib_admin_key");
    setAdminKey("");
    setAuthed(false);
  };

  // ───────────── Password gate ─────────────
  if (!authed) {
    return (
      <div
        className="min-h-screen bg-[#F7F9FB] flex items-center justify-center p-4"
        data-testid="debug-exp-gate"
      >
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-soft border border-[#E5E7EB] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#EEF3FF] flex items-center justify-center">
              <Lock size={18} className="text-[#2F5BFF]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1F3A5F]">Debug · Experiments</h1>
              <p className="text-xs text-[#6B7280]">Admin password required</p>
            </div>
          </div>
          <Input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin password"
            className="rounded-xl mb-3"
            data-testid="debug-exp-password"
            onKeyDown={(e) => e.key === "Enter" && verify(adminKey)}
          />
          <Button
            onClick={() => verify(adminKey)}
            disabled={loading}
            data-testid="debug-exp-unlock"
            className="w-full rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75]"
          >
            {loading ? "Verifying…" : "Unlock"}
          </Button>
          <Link
            to="/app"
            className="block mt-4 text-[12px] text-[#A0A7B4] hover:text-[#1F3A5F] text-center"
          >
            ← Back to app
          </Link>
        </div>
      </div>
    );
  }

  // ───────────── Main panel ─────────────
  return (
    <div className="min-h-screen bg-[#F7F9FB]" data-testid="debug-exp-page">
      <div className="sticky top-0 z-10 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link
            to="/app"
            className="text-[#6B7280] hover:text-[#1F3A5F] inline-flex items-center gap-2 text-sm font-medium"
            data-testid="debug-exp-back"
          >
            <ArrowLeft size={18} /> App
          </Link>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-[#2F5BFF]">
            <FlaskConical size={12} /> Debug
          </div>
          <button
            onClick={signOut}
            className="text-[11px] text-[#A0A7B4] hover:text-[#E07A7A]"
            data-testid="debug-exp-signout"
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div>
          <h1 className="text-xl font-bold text-[#1F3A5F]">Experiment overrides</h1>
          <p className="text-[13px] text-[#6B7280] leading-relaxed mt-1">
            Force a variant on <span className="font-semibold">this device only</span> to QA
            both arms before launch. Changes take effect on the next screen render.
          </p>
        </div>

        {EXPERIMENTS.map((exp) => {
          const current = assignments[exp.key];
          const serverRow = serverCounts[exp.key];
          const total = serverRow?.total ?? 0;
          // Prefer the deeper-funnel event when we have any data on it;
          // fall back to unlock_click for early-stage signals.
          const reserveData = serverRow?.conversions?.reserve_submit || {};
          const hasReserveData = Object.values(reserveData).some((n) => n > 0);
          const sigEventName = hasReserveData ? "reserve_submit" : "unlock_click";
          const rollup = exp.variants.map((v) => ({
            id: v.id,
            label: v.label,
            count: serverRow?.variants?.[v.id] ?? 0,
            conversions: serverRow?.conversions?.[sigEventName]?.[v.id] ?? 0,
          }));
          const sig =
            rollup.length === 2
              ? computeSignificance(rollup[0], rollup[1], sigEventName)
              : null;
          const winnerLabel =
            sig?.verdict === "winner"
              ? exp.variants.find((v) => v.id === sig.winnerId)?.label
              : null;
          return (
            <div
              key={exp.key}
              className="bg-white rounded-2xl border border-[#E5E7EB] shadow-soft p-5"
              data-testid={`debug-exp-card-${exp.key}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <h2 className="text-[15px] font-bold text-[#1F3A5F]">{exp.name}</h2>
                  <p className="text-[12px] text-[#6B7280] leading-snug">{exp.description}</p>
                </div>
                <div
                  className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${
                    current
                      ? "bg-[#EAF6F4] text-[#4FB3A5] border border-[#4FB3A5]/30"
                      : "bg-[#F7F9FB] text-[#A0A7B4] border border-[#E5E7EB]"
                  }`}
                >
                  {current ? `assigned: ${current}` : "unassigned"}
                </div>
              </div>

              {/* Significance verdict */}
              {sig && (
                <div
                  className={`mt-3 rounded-xl border px-3 py-2.5 flex items-center gap-2.5 ${
                    sig.verdict === "winner"
                      ? "bg-gradient-to-r from-[#EAF6F4] to-[#F0F9F7] border-[#4FB3A5]/40"
                      : "bg-[#FFF7E6] border-[#E9A13B]/30"
                  }`}
                  data-testid={`debug-exp-sig-${exp.key}`}
                >
                  {sig.verdict === "winner" ? (
                    <>
                      <div className="w-9 h-9 rounded-xl bg-[#4FB3A5] flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Trophy size={16} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-black text-[#1F3A5F] leading-tight">
                          Winner: {winnerLabel}
                          {sig.uplift ? (
                            <span className="text-[#4FB3A5] ml-1">
                              +{sig.uplift.toFixed(0)}% uplift
                            </span>
                          ) : null}
                        </div>
                        <div className="text-[11px] text-[#4FB3A5] font-semibold">
                          {sig.confidence} confidence · χ²={sig.chiSq.toFixed(2)} ·{" "}
                          <span className="font-normal text-[#4FB3A5]/80">
                            on {sig.eventName}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-9 h-9 rounded-xl bg-[#E9A13B]/15 flex items-center justify-center flex-shrink-0">
                        <HelpCircle size={16} className="text-[#B4762D]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-bold text-[#1F3A5F] leading-tight">
                          {sig.verdict === "needs_more"
                            ? `Needs more data — ${sig.minSize} devices / variant minimum`
                            : "Inconclusive — keep running"}
                        </div>
                        <div className="text-[11px] text-[#B4762D]">
                          {sig.verdict === "needs_more"
                            ? "Don't call a winner yet on low samples."
                            : sig.chiSq != null
                            ? `χ²=${sig.chiSq.toFixed(2)} · below 95% threshold (${CHI_SQ_95})`
                            : "Variants converting at the same rate so far."}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Live server counts */}
              <div
                className="mt-3 mb-1 rounded-xl bg-[#F7F9FB] border border-[#E5E7EB] p-3"
                data-testid={`debug-exp-counts-${exp.key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#A0A7B4]">
                    Live assignments · {total.toLocaleString()} devices
                  </span>
                  <span className="text-[10px] text-[#A0A7B4]">
                    {loadingCounts ? "loading…" : ""}
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {exp.variants.map((v) => {
                    const count = serverRow?.variants?.[v.id] ?? 0;
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    const unlocks = serverRow?.conversions?.unlock_click?.[v.id] ?? 0;
                    const convPct = count > 0 ? Math.round((unlocks / count) * 100) : 0;
                    const reserves = serverRow?.conversions?.reserve_submit?.[v.id] ?? 0;
                    const reservePct = count > 0 ? Math.round((reserves / count) * 100) : 0;
                    const isWinner = sig?.verdict === "winner" && sig?.winnerId === v.id;
                    return (
                      <div
                        key={v.id}
                        className={`flex-1 min-w-[120px] rounded-lg px-3 py-2 border ${
                          isWinner
                            ? "bg-[#EAF6F4] border-[#4FB3A5]"
                            : "bg-white border-[#E5E7EB]"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-bold text-[#1F3A5F] flex items-center gap-1">
                            {v.label}
                            {isWinner && <Trophy size={10} className="text-[#4FB3A5]" />}
                          </span>
                          <span className="text-[11px] font-semibold text-[#4FB3A5]">{pct}%</span>
                        </div>
                        <div className="text-[10px] text-[#6B7280] mt-0.5">
                          {count.toLocaleString()} device{count === 1 ? "" : "s"}
                        </div>
                        <div className="h-1 bg-[#E5E7EB] rounded-full mt-1.5 overflow-hidden">
                          <div
                            className="h-1 rounded-full bg-gradient-to-r from-[#4FB3A5] to-[#2F5BFF]"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="mt-2 pt-2 border-t border-[#F3F4F6] flex items-center justify-between">
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#A0A7B4]">
                            Unlock rate
                          </span>
                          <span className="text-[11px] font-black text-[#1F3A5F]">
                            {convPct}%
                            <span className="text-[9px] text-[#A0A7B4] font-normal ml-1">
                              ({unlocks}/{count})
                            </span>
                          </span>
                        </div>
                        <div className="mt-1 flex items-center justify-between">
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#4FB3A5]">
                            Reserve rate
                          </span>
                          <span className="text-[11px] font-black text-[#1F3A5F]">
                            {reservePct}%
                            <span className="text-[9px] text-[#A0A7B4] font-normal ml-1">
                              ({reserves}/{count})
                            </span>
                          </span>
                        </div>

                        {/* Funnel drop-off ribbon — reveals leak between click-through and actual conversion */}
                        {count > 0 && (
                          <div className="mt-2">
                            <div className="flex h-2 rounded-full overflow-hidden bg-[#F3F4F6]" title={`Reserved: ${reserves} · Unlocked only: ${unlocks - reserves} · No engagement: ${count - unlocks}`}>
                              <div
                                className="bg-[#2F5BFF] h-full"
                                style={{ width: `${reservePct}%` }}
                              />
                              <div
                                className="bg-[#4FB3A5] h-full"
                                style={{ width: `${Math.max(convPct - reservePct, 0)}%` }}
                              />
                            </div>
                            <div className="flex items-center justify-between mt-1 text-[9px] text-[#A0A7B4] leading-none">
                              <span className="inline-flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#2F5BFF]" />
                                Reserved
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#4FB3A5]" />
                                Unlocked only
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#E5E7EB]" />
                                Dropped
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {exp.variants.map((v) => {
                  const active = current === v.id;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setVariant(exp.key, v.id)}
                      data-testid={`debug-set-${exp.key}-${v.id}`}
                      className={`text-left rounded-xl border p-3 transition-all ${
                        active
                          ? "border-[#4FB3A5] bg-[#EAF6F4]"
                          : "border-[#E5E7EB] hover:border-[#1F3A5F]/40 hover:bg-[#F7F9FB]"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[13px] font-semibold text-[#1F3A5F]">{v.label}</span>
                        {active && (
                          <span className="text-[10px] font-bold text-[#4FB3A5] uppercase tracking-wider">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-[#6B7280] leading-snug">{v.blurb}</p>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#F3F4F6]">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => clearVariant(exp.key)}
                  data-testid={`debug-clear-${exp.key}`}
                  className="rounded-xl text-[12px] h-8 gap-1.5"
                >
                  <Trash2 size={12} /> Clear assignment
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { refreshAssignments(); refreshServerCounts(); }}
                  className="rounded-xl text-[12px] h-8 gap-1.5"
                >
                  <RefreshCw size={12} /> Refresh
                </Button>
                <span className="text-[10px] text-[#A0A7B4] ml-auto">
                  storage: <code>{STORAGE_PREFIX}{exp.key}</code>
                </span>
              </div>
            </div>
          );
        })}

        {/* Analytics source link */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#FFF3DC] flex items-center justify-center flex-shrink-0">
              <FlaskConical size={16} className="text-[#E9A13B]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[13px] font-bold text-[#1F3A5F]">Where to see real split & funnel</h3>
              <p className="text-[12px] text-[#6B7280] leading-snug mt-0.5">
                Aggregate assignment counts and per-variant Reserve conversions live in Firebase Analytics —
                this page only controls your <em>own</em> device. Filter events by parameter
                <code className="bg-[#F7F9FB] border border-[#E5E7EB] px-1 mx-1 rounded text-[11px]">variant</code>
                on <code className="bg-[#F7F9FB] border border-[#E5E7EB] px-1 mx-1 rounded text-[11px]">experiment_assignment</code> and
                <code className="bg-[#F7F9FB] border border-[#E5E7EB] px-1 mx-1 rounded text-[11px]">upgrade_click</code>.
              </p>
              <a
                href="https://console.firebase.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#2F5BFF] mt-2"
                data-testid="debug-exp-firebase-link"
              >
                Open Firebase console <ExternalLink size={11} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
